import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { Field, Input, Textarea, cn, type InputProps, type LucideIcon } from '@elbklar/ui';

/*
 * Textfelder des CRM: Field, Input und Textarea aus @elbklar/ui mit Label,
 * Hinweis und Fehler in einem Baustein, mit durchgereichtem ref für
 * react-hook-form (`register`, `Controller`). Auswahl und Checkbox kommen
 * direkt aus @elbklar/ui: `NativeSelect` in einem `Field`, `NativeCheckbox`.
 */

interface Beschriftung {
  label?: string;
  error?: string;
  /** Hinweis unter dem Feld. */
  hint?: string;
  /** Alter Name für hint. */
  helperText?: string;
}

function Rahmen({
  label,
  error,
  hint,
  required,
  className,
  children,
}: Beschriftung & { required?: boolean; className?: string; children: (f: { id?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode }) {
  const id = useId();
  if (!label) {
    return (
      <div className={cn('flex w-full flex-col gap-1.5', className)}>
        {children({ 'aria-invalid': error ? true : undefined, 'aria-describedby': error || hint ? `${id}-text` : undefined })}
        {(error || hint) && (
          <p id={`${id}-text`} className={cn('t-small', error ? 'text-danger' : 'text-muted-foreground')}>
            {error || hint}
          </p>
        )}
      </div>
    );
  }
  return (
    <Field label={label} error={error} hint={hint} required={required} className={cn('w-full', className)}>
      {children}
    </Field>
  );
}

export interface TextFieldProps extends Omit<InputProps, 'icon'>, Beschriftung {
  icon?: LucideIcon;
  /** Klassen für den Block aus Label, Feld und Hinweis. */
  className?: string;
}

/** Textfeld mit Label, Hinweis und Fehler. */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, helperText, className, required, icon, ...rest },
  ref,
) {
  return (
    <Rahmen label={label} error={error} hint={hint ?? helperText} required={required} className={className}>
      {(f) => <Input ref={ref} icon={icon} required={required} {...f} {...rest} id={rest.id ?? f.id} />}
    </Rahmen>
  );
});

export interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, Beschriftung {}

/** Mehrzeiliges Feld mit Label, Hinweis und Fehler. */
export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(function TextareaField(
  { label, error, hint, helperText, className, required, ...rest },
  ref,
) {
  return (
    <Rahmen label={label} error={error} hint={hint ?? helperText} required={required} className={className}>
      {(f) => <Textarea ref={ref} required={required} {...f} {...rest} id={rest.id ?? f.id} />}
    </Rahmen>
  );
});
